import React, {useId, useState} from 'react';
import {Button, Form, InputGroup, Stack} from "react-bootstrap";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ButtonDelete} from "../../_components";
import {useAuth} from "../../../context";
import {AdminUserAlert, OurClearAlert, SelectText, SingleText, ColumnizeText, CheckText} from "../../_components";
import {useCountryList} from "../admin/country/useCountry";
import {PageTitle} from "../../_components";
import {isoTimestampToReadableDate} from "../admin/utilities/readableDate";
import {Formik, Form as FormikForm, Field} from "formik";
import {useFetchJson, useGetJsonDirect} from "../../../hooks";
import {endpoints} from "../../../utils2";
import * as yup from "yup";
import {MyMapComponentIFrame} from "./PlatformMap";
import {useBrowserTitle} from "../../../hooks/useBrowserTitle";
import {WhiteCard} from "../../device/_components/WhiteCard";

export function AddEditAddress() {
    useBrowserTitle("Address");
    const {authToken, setVatPercentage} = useAuth()
    const {uid, aid} = useParams()
    const navigate = useNavigate()

    const initialValues = {
        name: "",
        prefix: "",
        phone: "",
        line1: "",
        country: "",
        region: "",
        city: "",
        zip: "",
        main: false,
    }

    const {
        value: preLoadValues,
        error: errorValue,
        clearError: clearErrorValue
    } = useGetJsonDirect(aid === "add" ? null : endpoints.address.get(uid, aid), authToken?.jwt, initialValues)

    const {exec: execAdd, error: errAdd, clearError: clearErrAdd} = useFetchJson(authToken?.jwt, null, "POST")
    const {exec: execEdit, error: errEdit, clearError: clearErrEdit} = useFetchJson(authToken?.jwt, null, "PUT")
    const {exec: execDelete, error: errDelete, clearError: clearErrDelete} = useFetchJson(authToken?.jwt, null, "DELETE")

    const {exec: getUserVat, error: errUserVat, clearError: clearErrUserVat} = useFetchJson(authToken?.jwt, null, "GET")

    const validationSchema = yup.object().shape({
        name: yup.string().required("Contact name cannot be empty"),
        prefix: yup.number().required("Missing prefix"),
        phone: yup.number().required("Missing phone"),
        line1: yup.string().required('Line1 is required'),
        country: yup.string().required('Country cannot be empty'),
        region: yup.string().required('Region is required'),
        city: yup.string().required('City cannot be empty'),
        zip: yup.string().required("Zip is required"),
    })

    const edit = aid !== "add"

    const {list} = useCountryList(100)

    function handleSubmit(values, actions) {
        if (edit) {
            execEdit(endpoints.address.edit(uid, aid), values, resp => {
                if (resp.data.main) {
                    getUserVat(endpoints.user.vat(uid), null, resp => {
                        setVatPercentage(resp.data.vat_percentage, true)
                    })
                }
                actions.setSubmitting(false)
                // Do not use -1 because it does not work with iframe
                navigate(`/backend/address/${uid}`)
            })
        } else {
            execAdd(endpoints.address.add(uid), values, resp => {
                if (resp.data.main) {
                    getUserVat(endpoints.user.vat(uid), null, resp => {
                        setVatPercentage(resp.data.vat_percentage, true)
                    })
                }
                actions.setSubmitting(false)
                navigate(`/backend/address/${uid}`)
            })
        }
    }

    function handleOnDelete() {
        execDelete(endpoints.address.delete(uid, aid), null, () => {
            getUserVat(endpoints.user.vat(uid), null, resp => {
                setVatPercentage(resp.data.vat_percentage, true)
            })
            navigate(-1)
        })
    }

    return (
        <>
            <AdminUserAlert/>
            <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>
            <OurClearAlert variant="danger" msg={errDelete} onClear={clearErrDelete}/>
            <OurClearAlert variant="danger" msg={errUserVat} onClear={clearErrUserVat}/>

            <PageTitle title="Address Details">{edit ? "Edit" : "Add"} address.</PageTitle>

            <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={preLoadValues} enableReinitialize={true}>
                {({isSubmitting, resetForm, setSubmitting}) => (
                    <FormikForm>

                        <OurClearAlert variant="danger" msg={errAdd} onClear={() => {
                            clearErrAdd()
                            setSubmitting(false)
                        }}/>
                        <OurClearAlert variant="danger" msg={errEdit} onClear={() => {
                            clearErrEdit()
                            setSubmitting(false)
                        }}/>

                        <WhiteCard>
                            <div className="fs-5 fw-semibold">Contact</div>
                            <ColumnizeText>
                                <SingleText name="name" label="Personal or Company Contact" maxLength="64" placeholder="AltraBits"/>
                                <PrefixPhone/>
                            </ColumnizeText>
                        </WhiteCard>

                        <WhiteCard>
                            <div className="fs-5 fw-semibold">Address</div>
                            <ColumnizeText>
                                <SingleText name="line1" label="Street, house/apartment/unit" placeholder="Via Roma"/>
                                <SingleText name="line2" label="Apt., suite, unit, etc (optional)"/>
                            </ColumnizeText>

                            <ColumnizeText>
                                <SelectText name="country" label="Select country">
                                    {list.map(item => (
                                        <option key={item.code} value={item.code}>{item.name}</option>
                                    ))}
                                </SelectText>
                                <SingleText name="region" label="State/province/region" placeholder="Italy"/>
                            </ColumnizeText>

                            <ColumnizeText className="">
                                <SingleText name="city" label="City" placeholder="Milan"/>
                                <SingleText name="zip" label="Postal code/ZIP code" placeholder="0101"/>
                            </ColumnizeText>

                            <SingleText name="note" type="textarea" label="Delivery instructions" className="mt-4"
                                        placeholder="Deliver near the blue door on the terrace"/>
                        </WhiteCard>

                        <CheckText name="main" label="Make this my default address" className="mt-4"/>


                        <Stack className="my-2 text-secondary">
                            {preLoadValues?.created_at && <span>Created at: {isoTimestampToReadableDate(preLoadValues.created_at)}</span>}
                            {preLoadValues?.updated_at && <span>Updated at: {isoTimestampToReadableDate(preLoadValues.updated_at)}</span>}
                        </Stack>

                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                            <Button as={Link} variant="link" to={-1}>Go back</Button>
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            {edit && <ButtonDelete variant="danger" onDelete={handleOnDelete} value={preLoadValues.name}>Delete</ButtonDelete>}
                            <Button type="submit" variant="success" disabled={isSubmitting}>Save</Button>
                        </Stack>

                        <MyMapComponentIFrame countries={list} />

                    </FormikForm>
                )}
            </Formik>

        </>
    );
}


function PrefixPhone() {
    const [z, setZ] = useState(true);

    return <div className="mb-2 align-items-start">
        {/*<div className="py-3"/>*/}
        <Form.Label column="" className="text-capitalize text-secondary-emphasis">Prefix & phone number</Form.Label>
        <Stack direction="horizontal" className="align-items-start">
            <Form.Group>
                <InputGroup.Text className="rounded-0 rounded-start border-end-0">+</InputGroup.Text>
            </Form.Group>
            <Field name="prefix">
                {({field, meta}) => (
                    <Form.Group controlId={useId()} className={`w-25 ${z ? "z-1" : "z-0"}`}>
                        <Form.Control {...field} maxLength={6}
                                      className="rounded-0 border-end-0"
                                      isInvalid={meta.touched && !!meta.error}
                                      onFocus={() => setZ(true)}
                                      autoComplete="tel-country-code"
                                      placeholder="39"
                        />
                        <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                    </Form.Group>
                )}
            </Field>
            <Field name="phone">
                {({field, meta}) => (
                    <Form.Group className={`flex-grow-1 ${z ? "z-0" : "z-1"}`}>
                        <Form.Control {...field} maxLength={12}
                                      className="rounded-0 rounded-end"
                                      isInvalid={meta.touched && !!meta.error}
                                      onFocus={() => setZ(false)}
                                      autoComplete="tel-national"
                                      placeholder="12345678"
                        />
                        <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                    </Form.Group>
                )}
            </Field>
        </Stack>

        {/*<Form.Text muted className="">Prefix and phone number</Form.Text>*/}
    </div>
}
